import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Whether this browser session holds a Claude connection.
 *
 * Identity and execution are deliberately separate in this app: Microsoft SSO
 * says who you are and grants read access to every audit, while a Claude
 * connection is what a RUN executes on — billed to the operator's own
 * subscription rather than a shared Nymbl key. So signing in does not connect
 * Claude, and starting an audit asks for it.
 *
 * Session-scoped React state, never persisted. That is not a shortcut: the
 * design's whole claim is that no token is stored against a Nymbl account, and
 * writing this to localStorage would quietly make that claim false. A page
 * reload dropping the connection is the correct behaviour.
 *
 * The connection is not yet wired to a real credential exchange — `connect()`
 * records intent so the flow can be reviewed end to end. Every surface that
 * shows connection state reads it from here, so replacing this provider with a
 * real one changes no screen.
 */

interface ClaudeConnection {
  connected: boolean;
  /** The Claude account a run would bill against, once connected. */
  account: string | null;
  /** When the connection was established, for the Account screen. */
  connectedAt: Date | null;
  connect: (account: string) => void;
  disconnect: () => void;
}

const ClaudeConnectionContext = createContext<ClaudeConnection>({
  connected: false,
  account: null,
  connectedAt: null,
  connect: () => {},
  disconnect: () => {},
});

export function ClaudeConnectionProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<string | null>(null);
  const [connectedAt, setConnectedAt] = useState<Date | null>(null);

  const connect = useCallback((next: string) => {
    setAccount(next);
    setConnectedAt(new Date());
  }, []);

  const disconnect = useCallback(() => {
    setAccount(null);
    setConnectedAt(null);
  }, []);

  const value = useMemo(
    () => ({ connected: account !== null, account, connectedAt, connect, disconnect }),
    [account, connectedAt, connect, disconnect],
  );

  return (
    <ClaudeConnectionContext.Provider value={value}>{children}</ClaudeConnectionContext.Provider>
  );
}

export function useClaudeConnection() {
  return useContext(ClaudeConnectionContext);
}
